"use client";

import { publicStorePath } from "@/lib/storefront/public-url";
import { useState, useRef } from "react";
import { useFormatter, useTranslations } from "next-intl";
import Link from "next/link";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { identifyPosthogByEmail } from "@/lib/posthog-identify-email";
import { useHydrated } from "@/hooks/use-hydrated";
import { ContactFormNoScript } from "@/ui/components/contact-form-no-script";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const TOPICS = ["kit", "order", "documentation", "returns", "partnership", "other"] as const;
type ContactTopic = (typeof TOPICS)[number];
type ApiContactTopic = "orders" | "returns" | "partnership" | "other";

type FormStatus = "idle" | "loading" | "success" | "error";

export interface ContactOrderOption {
	id: string;
	number: string;
	created: string;
}

export interface ContactFormProps {
	locale: string;
	channel: string;
	mode?: "public" | "account";
	defaultName?: string;
	defaultEmail?: string;
	orders?: ContactOrderOption[];
	initialOrderId?: string | null;
}

// ---------------------------------------------------------------------------
// Field label key helper
// ---------------------------------------------------------------------------

function topicKey(topic: ContactTopic): string {
	return `contact.topic${topic.charAt(0).toUpperCase()}${topic.slice(1)}`;
}

function mapContactTopicToApi(topic: ContactTopic): ApiContactTopic {
	if (topic === "order") return "orders";
	if (topic === "returns") return "returns";
	if (topic === "partnership") return "partnership";
	return "other";
}

function initialOrderNumberFromProps(
	orders: ContactOrderOption[] | undefined,
	initialOrderId: string | null | undefined,
): string {
	if (!initialOrderId || !orders?.length) return "";
	const o = orders.find((x) => x.id === initialOrderId);
	return o?.number ?? "";
}

function initialSelectedOrderId(
	orders: ContactOrderOption[] | undefined,
	initialOrderId: string | null | undefined,
): string {
	if (!initialOrderId || !orders?.length) return "";
	return orders.some((x) => x.id === initialOrderId) ? initialOrderId : "";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ContactForm({
	locale,
	channel: _channel,
	mode = "public",
	defaultName = "",
	defaultEmail = "",
	orders,
	initialOrderId,
}: ContactFormProps) {
	const t = useTranslations("contact");
	const tKit = useTranslations("kitWebsite");
	const ta = useTranslations("account");
	const format = useFormatter();
	const privacyHref = `${publicStorePath(locale, "/privacy")}`;
	const isAccount = mode === "account";
	const isHydrated = useHydrated();

	const [name, setName] = useState(defaultName);
	const [email, setEmail] = useState(defaultEmail);
	const [topic, setTopic] = useState<ContactTopic | "">("");
	const [message, setMessage] = useState("");
	const [consent, setConsent] = useState(false);
	const [status, setStatus] = useState<FormStatus>("idle");
	const [errorKey, setErrorKey] = useState<"formError" | "formErrorRateLimit">("formError");
	const identifyDedupeRef = useRef<string | null>(null);

	const [orderNumber, setOrderNumber] = useState(() => initialOrderNumberFromProps(orders, initialOrderId));
	const [selectedOrderId, setSelectedOrderId] = useState(() =>
		initialSelectedOrderId(orders, initialOrderId),
	);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!isHydrated || status === "loading") return;
		setStatus("loading");
		const trimmedOrder = orderNumber.trim();
		const payload: Record<string, unknown> = {
			name,
			email,
			topic: topic ? mapContactTopicToApi(topic) : "other",
			message,
			consent,
			locale,
		};
		if (trimmedOrder) {
			payload.orderNumber = trimmedOrder;
		}
		if (isAccount) {
			payload.contactSource = "account_support";
		}
		try {
			const res = await fetch("/api/contact", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});
			if (!res.ok) {
				const data = (await res.json()) as { error?: string };
				setErrorKey(data?.error === "too_many_requests" ? "formErrorRateLimit" : "formError");
				setStatus("error");
				return;
			}
			identifyPosthogByEmail(
				email,
				{
					name,
					contact_source: isAccount ? "account_support" : "contact_form",
				},
				identifyDedupeRef,
			);
			setStatus("success");
		} catch {
			setErrorKey("formError");
			setStatus("error");
		}
	}

	function onOrderSelectChange(id: string) {
		setSelectedOrderId(id);
		if (id === "") {
			setOrderNumber("");
		} else {
			const o = orders?.find((x) => x.id === id);
			setOrderNumber(o?.number ?? "");
		}
	}

	function onOrderNumberInput(value: string) {
		setOrderNumber(value);
		setSelectedOrderId("");
	}

	// ---- Success state ----
	if (status === "success" && topic) {
		const label = tKit(topicKey(topic) as Parameters<typeof tKit>[0]);
		return (
			<div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
				<div className="flex flex-col items-center gap-4 py-6 text-center">
					<div className="bg-accent/10 flex h-14 w-14 items-center justify-center rounded-full">
						<CheckCircle2 className="h-7 w-7 text-accent" aria-hidden="true" />
					</div>
					<div className="space-y-1">
						<p className="text-base font-semibold text-foreground">{t("formSuccess")}</p>
						<p className="mx-auto max-w-xs text-sm leading-relaxed text-muted-foreground">
							{t("formSuccessDesc", { name, topic: label })}
						</p>
					</div>
				</div>
			</div>
		);
	}

	const formTitle = isAccount ? ta("supportFormTitle") : t("formTitle");

	// ---- Form ----
	return (
		<div className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
			<h2 className="mb-6 font-display text-[1.2rem] font-medium leading-tight tracking-[-0.012em] text-foreground">
				{formTitle}
			</h2>

			<form method="post" onSubmit={(e) => void handleSubmit(e)} className="space-y-5" noValidate>
				{/* Name + Email — side by side on sm+ */}
				<div className="grid gap-5 sm:grid-cols-2">
					<div className="space-y-1.5">
						<label
							htmlFor="contact-name"
							className="block font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
						>
							{t("formName")}
						</label>
						<input
							id="contact-name"
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
							maxLength={100}
							autoComplete="name"
							className="w-full rounded-xl border border-border bg-background px-4 py-3 text-[0.95rem] text-foreground transition-colors placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
						/>
					</div>

					<div className="space-y-1.5">
						<label
							htmlFor="contact-email"
							className="block font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
						>
							{t("formEmail")}
						</label>
						<input
							id="contact-email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							autoComplete="email"
							className="w-full rounded-xl border border-border bg-background px-4 py-3 text-[0.95rem] text-foreground transition-colors placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
						/>
					</div>
				</div>

				{isAccount && orders && orders.length > 0 && (
					<div className="space-y-1.5">
						<label
							htmlFor="support-order-select"
							className="block font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
						>
							{ta("supportOrderSelectLabel")}
						</label>
						<select
							id="support-order-select"
							value={selectedOrderId}
							onChange={(e) => onOrderSelectChange(e.target.value)}
							className={cn(
								"w-full rounded-xl border border-border bg-background px-4 py-3 text-[0.95rem] transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring",
								selectedOrderId === "" ? "text-muted-foreground" : "text-foreground",
							)}
						>
							<option value="">{ta("supportOrderSelectPlaceholder")}</option>
							{orders.map((o) => {
								const shortDate = format.dateTime(new Date(o.created), {
									year: "numeric",
									month: "short",
									day: "numeric",
								});
								return (
									<option key={o.id} value={o.id}>
										{ta("supportOrderSelectOption", { number: o.number, date: shortDate })}
									</option>
								);
							})}
						</select>
					</div>
				)}

				{isAccount && (
					<div className="space-y-1.5">
						<label
							htmlFor="support-order-number"
							className="block font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
						>
							{ta("supportOrderNumberLabel")}
						</label>
						<input
							id="support-order-number"
							type="text"
							value={orderNumber}
							onChange={(e) => onOrderNumberInput(e.target.value)}
							maxLength={64}
							autoComplete="off"
							placeholder={ta("supportOrderNumberPlaceholder")}
							className="w-full rounded-xl border border-border bg-background px-4 py-3 text-[0.95rem] text-foreground transition-colors placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
						/>
						<p className="text-xs text-muted-foreground">{ta("supportOrderNumberHint")}</p>
					</div>
				)}

				{/* Topic */}
				<div className="space-y-1.5">
					<label
						htmlFor="contact-topic"
						className="block font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
					>
						{t("formTopic")}
					</label>
					<select
						id="contact-topic"
						value={topic}
						onChange={(e) => setTopic(e.target.value as ContactTopic | "")}
						required
						className={cn(
							"w-full rounded-xl border border-border bg-background px-4 py-3 text-[0.95rem] transition-colors focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring",
							topic === "" ? "text-muted-foreground" : "text-foreground",
						)}
					>
						<option value="" disabled>
							{t("formTopicPlaceholder")}
						</option>
						{TOPICS.map((key) => (
							<option key={key} value={key}>
								{tKit(topicKey(key) as Parameters<typeof tKit>[0])}
							</option>
						))}
					</select>
				</div>

				{/* Message */}
				<div className="space-y-1.5">
					<label
						htmlFor="contact-message"
						className="block font-mono text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
					>
						{t("formMessage")}
					</label>
					<textarea
						id="contact-message"
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						required
						minLength={10}
						maxLength={2000}
						rows={5}
						placeholder={tKit("contact.messagePlaceholder")}
						className="min-h-[120px] w-full resize-y rounded-xl border border-border bg-background px-4 py-3 text-[0.95rem] text-foreground transition-colors placeholder:text-muted-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-ring"
					/>
				</div>

				{/* Consent */}
				<label className="flex cursor-pointer items-start gap-3">
					<input
						type="checkbox"
						checked={consent}
						onChange={(e) => setConsent(e.target.checked)}
						required
						className="mt-0.5 h-4 w-4 shrink-0 rounded border border-border bg-background accent-primary"
					/>
					<span className="text-xs leading-relaxed text-muted-foreground">
						{t("formConsentPrefix")}
						<Link href={privacyHref} className="underline underline-offset-2 hover:text-accent-dark">
							{t("formConsentPrivacy")}
						</Link>
						{t("formConsentSuffix")}
					</span>
				</label>

				{/* Error message */}
				{status === "error" && (
					<p className="text-xs text-destructive" role="alert">
						{t(errorKey)}
					</p>
				)}

				{/* Submit — brand pill */}
				<ContactFormNoScript />
				<button
					type="submit"
					disabled={!isHydrated || status === "loading"}
					className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-primary-hero font-mono text-[0.75rem] font-semibold uppercase tracking-[0.2em] text-primary-hero-foreground shadow-md transition-colors hover:bg-primary disabled:opacity-70"
				>
					{status === "loading" ? (
						<>
							<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
							{t("formSubmitting")}
						</>
					) : (
						<>
							<Send className="h-4 w-4" aria-hidden="true" />
							{t("formSubmit")}
						</>
					)}
				</button>
			</form>
		</div>
	);
}
